import { createPortal } from "react-dom";
import type { ChartMeasure } from "../types/domain";
import type { ChordView } from "../core/theory/transpose";
import type { StyledChart } from "../hooks/useStyledChart";
import { chordShape } from "../core/theory/chords";
import { measureSegments, usedChords } from "../core/grid/segments";
import { isDenseBar } from "../core/export/chartText";
import { ChordDiagram } from "./ChordDiagram";
import { ChordName } from "./ChordName";
import { useT } from "../i18n";
import { noteGlyphs } from "./keyText";

interface Props {
  title: string;
  meta: string;
  capo: number;
  diagrams: boolean;
  measures: ChartMeasure[];
  view: ChordView;
  /** a play style other than 원래대로: its names and fingerings replace the plain ones */
  chart?: StyledChart;
  url: string | null;
}

// The printed chord chart (A4, 4 bars per line). Lives outside the app root and is shown only by the
// print stylesheet; the browser paginates, rows never split across pages.
export function PrintSheet({ title, meta, capo, diagrams, measures, view, chart, url }: Props) {
  const t = useT();
  const rows: ChartMeasure[][] = [];
  for (let i = 0; i < measures.length; i += 4) rows.push(measures.slice(i, i + 4));
  return createPortal(
    <div className="print-root" aria-hidden="true">
      <header className="pr-head">
        <div>
          <h1 className="pr-title">{title}</h1>
          <p className="pr-meta">{meta}</p>
        </div>
        {capo > 0 && <div className="pr-capo">{t("key.capo_tag", { n: capo })}</div>}
      </header>
      {diagrams && (
        <div className="pr-dias">
          {chart
            ? chart.diagrams.map((d) => (
              <ChordDiagram key={d.key} frets={d.frets} label={d.name} added={d.added} marks={d.marks}
                from={d.from ? t("style.from", { c: noteGlyphs(d.from) }) : null} />
            ))
            : usedChords(measures).map((ch) => {
              const shape = chordShape(view.shape(ch));
              return shape ? <ChordDiagram key={ch} frets={shape.frets} label={view.name(ch)} /> : null;
            })}
        </div>
      )}
      <div className="pr-grid">
        {rows.map((row, r) => (
          <div className="pr-row" key={r}>
            {[0, 1, 2, 3].map((j) => {
              const m = row[j];
              if (!m) return <div className="pr-bar blank" key={j} />;
              const n = r * 4 + j;
              const segs = measureSegments(m);
              const shown = segs.map((s, k) => (s.chord ? chart?.at(n, k) ?? null : null));
              const names = segs.map((s, k) => (s.chord ? shown[k]?.name ?? view.name(s.chord) : "–"));
              return (
                <div className={"pr-bar" + (isDenseBar(names) ? " dense" : "") + (n === measures.length - 1 ? " end" : "")} key={j}>
                  <span className="pr-num">{n + 1}</span>
                  {segs.map((s, k) => (
                    <span className="pr-seg" key={k} style={{ flexGrow: s.beats }}>{s.chord ? <ChordName chord={names[k]} added={shown[k]?.added} /> : names[k]}</span>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <footer className="pr-foot">{t("export.footer")}{url ? ` · ${url}` : ""}</footer>
    </div>,
    document.body,
  );
}
